import React, { useEffect, useMemo, useState } from "react";
import Comments from "./Comments";

type BlogPost = {
  id?: string;
  ID?: string;
  Title?: string;
  Body?: string;
  date?: string;
  subject?: string;
  images?: string | string[] | null;
  links?: string[] | string | null;
  youtubeId?: string;
  comments?: any[];
};

type Props = {
  postId: string;
  onBack?: () => void;
};

// Reuse helpful utilities (duplicated here for isolation/minimal changes)
function removeHtmlTags(str: string) {
  return String(str || "").replace(/<[^>]*>?/gm, "");
}
function hasEmbeddable(html: string | null | undefined): boolean {
  if (!html) return false;
  return /<(object|embed|iframe|video|audio)\b/i.test(html);
}
function sanitizeHtmlAllowEmbeds(html: string): string {
  let out = String(html || "");
  out = out.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
  out = out.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  out = out.replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[^'\"]*\2/gi, ' $1="#"');
  return out;
}
function extractDisplayImageSrc(htmlOrArray: string | string[] | null | undefined): string | null {
  if (!htmlOrArray) return null;
  if (Array.isArray(htmlOrArray)) {
    const first = htmlOrArray.find((s) => typeof s === "string" && s.trim().length > 0);
    if (!first) return null;
    const raw = first.trim();
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^\/?images\//i.test(raw)) return raw.startsWith("/") ? raw : `/${raw}`;
    if (raw.startsWith("data:")) return raw;
  }
  const val = Array.isArray(htmlOrArray) ? String(htmlOrArray[0] ?? "") : String(htmlOrArray);
  const s = val.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^data:/i.test(s)) return s;
  if (/^\/?images\//i.test(s)) return s.startsWith("/") ? s : `/${s}`;
  const imgTagMatch = s.match(/<img\b[^>]*src=["']([^"'>\s]+)["'][^>]*>/i);
  if (!imgTagMatch) return null;
  const raw = imgTagMatch[1];
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^\/?images\//i.test(raw)) return raw.startsWith("/") ? raw : `/${raw}`;
  return null;
}

// Extract/clean a YouTube id from a raw id or URL
function safeYouTubeId(raw: any): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    if (/^(www\.)?youtu\.be$/i.test(u.hostname)) {
      const id = u.pathname.replace(/^\//, "");
      const clean = id.replace(/[^a-zA-Z0-9_-]/g, "");
      return clean || null;
    }
    if (/^(www\.)?youtube\.com$/i.test(u.hostname)) {
      const id = u.searchParams.get("v") || "";
      const clean = id.replace(/[^a-zA-Z0-9_-]/g, "");
      if (clean) return clean;
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "embed" && parts[1]) {
        const emb = parts[1].replace(/[^a-zA-Z0-9_-]/g, "");
        return emb || null;
      }
    }
  } catch {}
  const cleaned = s.replace(/[^a-zA-Z0-9_-]/g, "");
  return cleaned || null;
}

const BlogPostPage: React.FC<Props> = ({ postId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/blog/${encodeURIComponent(postId)}`);
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `Failed to load (${res.status})`);
        }
        const json = (await res.json()) as BlogPost;
        if (!cancelled) setPost(json);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load post");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const title = removeHtmlTags(post?.Title || "");
  const mainImage = useMemo(() => extractDisplayImageSrc(post?.images || null), [post?.images]);

  async function copyLink() {
    try {
      const url = window.location.href;
      // Use a safe, type-friendly check for Web Share API on mobile
      const canWebShare = typeof window !== "undefined" && typeof navigator !== "undefined" && typeof (navigator as any).share === "function" && /Mobi|Android/i.test(navigator.userAgent);
      if (canWebShare) {
        await (navigator as any).share({ title: title || "Post", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  }

  return (
    <section className="post-page">
      <div className="pp-actions">
        <button className="btn" onClick={onBack}>
          ← Back to Blog
        </button>
        <button className="btn" onClick={copyLink}>
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </div>

      {loading ? (
        <div className="pp-status">Loading…</div>
      ) : error ? (
        <div className="pp-error" role="alert">
          {error}
        </div>
      ) : !post ? (
        <div className="pp-status">Post not found.</div>
      ) : (
        <article className="pp-card">
          <header className="pp-header">
            <img src="/dagger.png" alt="" className="pp-dagger" />
            <h1 className="pp-title">{title}</h1>
          </header>
          {post.date && <div className="post-meta">{post.date}</div>}
          {mainImage && <img className="post-image" src={mainImage} alt="Post" />}
          {/* Embed YouTube when a valid id exists */}
          {(() => {
            const y = safeYouTubeId(post?.youtubeId);
            if (!y) return null;
            return (
              <div className="post-video">
                <div className="video-wrap">
                  <iframe src={`https://www.youtube.com/embed/${y}`} title={title || "YouTube video"} frameBorder={0} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </div>
            );
          })()}
          {hasEmbeddable(post.Body || "") ? <div className="post-html" dangerouslySetInnerHTML={{ __html: sanitizeHtmlAllowEmbeds(post.Body || "") }} /> : <p className="post-body">{removeHtmlTags(post.Body || "")}</p>}
          {/* Links list if multiple */}
          {(() => {
            const links = Array.isArray(post.links)
              ? post.links.filter((s) => typeof s === "string" && s.trim().length > 0)
              : typeof post.links === "string"
              ? post.links
                  .split(",")
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0)
              : [];
            if (links.length > 1) {
              return (
                <div className="post-links" aria-label="Related links">
                  <div className="post-links-title">Links</div>
                  <ul className="post-links-list">
                    {links.map((u, i) => (
                      <li key={`${i}-${u}`}>
                        <a href={u} target="_blank" rel="noopener noreferrer">
                          {u}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            return null;
          })()}

          {(() => {
            // Prefer the route param as the canonical id; fallback to any id fields on the post
            const pid = String(postId || post?.id || post?.ID || "").trim();
            if (!pid) return null;
            return <Comments postId={pid} initialComments={Array.isArray(post.comments) ? post.comments : []} editMode={false} defaultOpen={true} />;
          })()}
        </article>
      )}

      <style>{`
        .post-page { text-align: left; }
        .pp-actions { display: flex; gap: 10px; margin-bottom: 12px; }
        .pp-status { font-style: italic; }
        .pp-error { color: #b00020; font-weight: 800; }
        .pp-card {
          border: 3px solid #000;
          background: #fff;
          box-shadow: 6px 6px 0 #000;
          padding: 16px;
          position: relative;
        }
        .pp-header { display: flex; align-items: center; gap: 10px; }
        .pp-dagger { width: 52px; height: auto; }
        .pp-title { margin: 0; font-weight: 900; line-height: 1.15; }
        /* Button styles (match blog list styling) */
        .btn {
          border: 3px solid #000;
          box-shadow: 3px 3px 0 #000;
          background: #fff;
          padding: 6px 10px;
          font-weight: 800;
          cursor: pointer;
        }
        .btn-primary { background: #000; color: #fff; }
        .btn-danger { background: #b00020; color: #fff; }
        .btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .post-meta {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.9rem;
          border: 2px solid #000;
          display: inline-block;
          padding: 2px 6px;
          margin: 8px 0 10px;
          background: #f8f8f8;
        }
        .post-image { width: 100%; height: auto; display: block; margin: 0 0 10px; }
        .post-body { margin: 0; line-height: 1.6; }
        .post-links { margin-top: 10px; }
        .post-links-title { font-weight: 800; margin-bottom: 4px; }
        .post-links-list { margin: 0; padding-left: 18px; }
        /* Responsive YouTube embed (match list page) */
        .post-video, .post-video .video-wrap {
          width: 100%;
          aspect-ratio: 16 / 9;
          background: #000;
          margin: 0 0 10px;
          position: relative;
          overflow: hidden;
        }
        .post-video iframe, .post-video .video-wrap iframe {
          width: 100%;
          height: 100%;
          border: 0;
          position: absolute;
          inset: 0;
          display: block;
        }
      `}</style>
    </section>
  );
};

export default BlogPostPage;
